<script setup lang="ts">
import { ROUTES } from '~/utils/constants'
import { z } from 'zod'
import type { FormSubmitEvent } from '#ui/types'

definePageMeta({
  layout: 'auth',
  path: '/login',
  middleware: 'guest',
})

const auth = useAuth()

useSiteSeoMeta({
  title: 'Sign In',
  description: 'Sign in to your TCF Canada account to continue your French learning journey.',
})

const loading = ref(false)
const showPassword = ref(false)
const errorMsg = ref('')

const schema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(1, 'Password is required'),
})

type Schema = z.output<typeof schema>

const state = reactive({
  email: '',
  password: '',
})

const handleLogin = async (event: FormSubmitEvent<Schema>) => {
  loading.value = true
  errorMsg.value = ''
  try {
    await auth.signIn.email(
      {
        email: state.email,
        password: state.password,
        callbackURL: ROUTES.DASHBOARD,
      },
      {
        onSuccess: (ctx: any) => {
          if (ctx.response.status === 200 || ctx.response.status === 201) {
            const router = useRouter()
            router.push(ROUTES.DASHBOARD)
            return
          }
          console.error('Unexpected status code:', ctx.response.status)
        },
        onError: (ctx: any) => {
          console.error('Login error:', ctx.error)
          errorMsg.value = ctx.error.message || 'Invalid email or password'
        },
      },
    )
  } catch (error: any) {
    console.error(error)
    errorMsg.value = 'An unexpected error occurred'
  } finally {
    loading.value = false
  }
}

const handleGoogleLogin = async () => {
  await auth.signIn.social({
    provider: 'google',
    callbackURL: ROUTES.DASHBOARD,
  })
}

const handleMicrosoftLogin = async () => {
  await auth.signIn.social({
    provider: 'microsoft',
    callbackURL: ROUTES.DASHBOARD,
  })
}

const handleLinkedinLogin = async () => {
  await auth.signIn.social({
    provider: 'linkedin',
    callbackURL: ROUTES.DASHBOARD,
  })
}
</script>

<template>
  <div class="flex flex-col gap-6 w-full max-w-sm mx-auto">
    <div class="text-center mb-2">
      <div
        class="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-primary-100 text-primary-600 mb-4 dark:bg-primary-900/30 dark:text-primary-400"
      >
        <UIcon name="i-heroicons-arrow-right-on-rectangle" class="w-6 h-6" />
      </div>
      <h1 class="text-2xl font-bold text-gray-900 dark:text-white mb-1">Welcome Back!</h1>
      <p class="text-sm text-gray-500 dark:text-gray-400">
        Sign in to continue your learning journey
      </p>
    </div>

    <UForm :schema="schema" :state="state" @submit="handleLogin" class="flex flex-col gap-4">
      <Transition appear>
        <UAlert
          v-if="errorMsg"
          color="error"
          variant="soft"
          icon="i-heroicons-exclamation-triangle"
          :title="errorMsg"
          class="mb-2 animate-pulse"
        />
      </Transition>

      <UFormField label="Email" name="email" class="font-medium">
        <UInput
          id="login-email"
          v-model="state.email"
          type="email"
          placeholder="you@example.com"
          size="xl"
          icon="i-heroicons-envelope"
          class="w-full"
        />
      </UFormField>

      <UFormField label="Password" name="password" class="font-medium">
        <UInput
          id="login-password"
          v-model="state.password"
          :type="showPassword ? 'text' : 'password'"
          placeholder="••••••••"
          size="xl"
          icon="i-heroicons-lock-closed"
          class="w-full"
        >
          <template #trailing>
            <UButton
              color="neutral"
              variant="link"
              :icon="showPassword ? 'i-heroicons-eye-slash' : 'i-heroicons-eye'"
              :padded="false"
              :class="{ 'text-primary-500': showPassword }"
              @click="
                () => {
                  showPassword = !showPassword
                }
              "
              class="hover:text-primary-500 transition-colors"
            />
          </template>
        </UInput>
      </UFormField>

      <div class="flex justify-end">
        <UButton
          :to="ROUTES.FORGOT_PASSWORD"
          variant="link"
          color="primary"
          :padded="false"
          class="font-medium text-sm hover:underline"
          >Forgot password?</UButton
        >
      </div>

      <UButton
        type="submit"
        block
        size="xl"
        color="primary"
        class="font-bold shadow-lg shadow-primary-500/20 mt-2"
        :loading="loading"
      >
        Sign In
      </UButton>
    </UForm>

    <div class="relative py-2 mt-2">
      <div class="absolute inset-0 flex items-center">
        <span class="w-full border-t border-gray-200 dark:border-gray-800" />
      </div>
      <div class="relative flex justify-center text-xs uppercase font-bold tracking-wider">
        <span class="bg-white dark:bg-gray-950 px-3 text-gray-400">Or continue with</span>
      </div>
    </div>

    <div class="grid grid-cols-1 gap-3">
      <UButton
        block
        size="lg"
        color="neutral"
        variant="outline"
        icon="i-simple-icons-google"
        class="text-gray-700 dark:text-gray-200 dark:bg-gray-900 dark:hover:bg-gray-800 border-gray-200 dark:border-gray-800 font-semibold shadow-sm transition-all hover:scale-[1.02] active:scale-[0.98]"
        @click="handleGoogleLogin"
      >
        Google
      </UButton>
      <div class="grid grid-cols-2 gap-3">
        <UButton
          block
          size="lg"
          color="neutral"
          variant="outline"
          icon="i-simple-icons-microsoft"
          class="text-gray-700 dark:text-gray-200 dark:bg-gray-900 dark:hover:bg-gray-800 border-gray-200 dark:border-gray-800 font-semibold shadow-sm transition-all hover:scale-[1.02] active:scale-[0.98]"
          @click="handleMicrosoftLogin"
        >
          Microsoft
        </UButton>
        <UButton
          block
          size="lg"
          color="neutral"
          variant="outline"
          icon="i-simple-icons-linkedin"
          class="text-gray-700 dark:text-gray-200 dark:bg-gray-900 dark:hover:bg-gray-800 border-gray-200 dark:border-gray-800 font-semibold shadow-sm transition-all hover:scale-[1.02] active:scale-[0.98]"
          @click="handleLinkedinLogin"
        >
          LinkedIn
        </UButton>
      </div>
    </div>

    <p class="text-center text-sm text-gray-500 dark:text-gray-400 mt-4">
      Don't have an account?
      <UButton
        :to="ROUTES.SIGNUP"
        variant="link"
        color="primary"
        :padded="false"
        class="font-bold underline"
        >Sign up</UButton
      >
    </p>
  </div>
</template>
